import { DIRS, hexDist, hexLen, hexToWorld, ROW, type Hex } from '../hex.ts';
import { dist2, idiv, imax, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The cleric's last road, chapter 1 (docs/sendas/clerigo.md, «La camilla»): its grounds, the same from the seed in the
 * sim and the drawing, integers only. Up is −y (the top of the screen).
 *
 * - `streetLayout` (act 1, the lower street): Musgoviejo's lane three hexes wide winding up the board between shut
 *   houses, from the little square at the foot to the village's arch at the top; alleys cut into the houses here and
 *   there (where the stalkers come out).
 * - `chapelLayout` (act 2, the chapel at the crossroads): the shepherds' terraces, open; at the top the chapel, its door
 *   with its lamp; two hooks for the lantern and two mirrors on their posts. Of the eighteen ways to set them (a hook,
 *   three positions a mirror) one lights the lamp, and the hook that seems nearer (in line with the lamp) sends the beam
 *   into a mirror that never lets it through straight. `chapelSolve` counts them.
 * - `bendsLayout` (act 3, the test): the hairpin bends up from the terraces to the cliff road, four legs across the
 *   board joined by three bends; the slope between them rock (the way is the only way); the limpets come out at the bends.
 *
 * The beam (`beamTrace`): from the hook the lantern hangs on, along one of the six directions, cell by cell; a mirror
 * always turns it (its position: a sixth to one side, a sixth to the other, or back where it came from: never straight
 * on), a wall or the board's edge stops it, the lamp is lit by it.
 */

export const STREET_RADIUS = 12;
export const CHAPEL_RADIUS = 6;
export const BENDS_RADIUS = 11;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};
const sortHex = (a: Hex, b: Hex): number => a.r - b.r || a.q - b.q;

/** Squared distance from a point to a segment (integers; t in thousandths along it). */
const segD2 = (px: number, py: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2) : 0;
  t = imax(0, imin(1000, t));
  return dist2(px, py, ax + idiv(dx * t, 1000), ay + idiv(dy * t, 1000));
};

/* ---------- the lower street ---------- */

export interface StreetLayout {
  radius: number;
  /** The houses (and the walls of the square): what can't be walked. */
  rocks: Hex[];
  /** The lane's cells, the square's at the foot, the alleys' (each two cells into the houses). */
  lane: Hex[];
  alleys: Hex[];
  /** The stretcher's way, row by row from the square to the arch. */
  way: { x: number; y: number }[];
  /** Where the stalkers come out: each alley's far cell and the way's point it opens by. */
  dens: { x: number; y: number; at: number }[];
  /** The arch at the top (its middle), where the stretcher is won. */
  arch: { x: number; y: number };
  start: { x: number; y: number };
}

export const streetLayout = (seed: number): StreetLayout => {
  const R = STREET_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc1e1));
  // The lane's middle on each row from the foot (r = R − 1) to the top (r = −(R − 1)): it winds a half hex a row.
  const mid = new Map<number, number>();
  let x = 0;
  let dir = rngInt(rng, 2) === 0 ? -1 : 1;
  for (let r = R - 1; r >= -(R - 1); r--) {
    mid.set(r, x);
    if (rngInt(rng, 10) < 3) dir = -dir;
    x = imax(-4000, imin(4000, x + dir * 500));
    if (Math.abs(x) === 4000) dir = -dir;
  }
  // Alleys: every three rows or so, on one side or the other, two cells into the houses.
  const alleyRows: { r: number; side: number }[] = [];
  for (let r = R - 4; r > -(R - 3); r -= 3 + rngInt(rng, 2)) alleyRows.push({ r, side: rngInt(rng, 2) === 0 ? -1 : 1 });
  const lane: Hex[] = [];
  const alleys: Hex[] = [];
  const rocks: Hex[] = [];
  for (const h of allCells(R)) {
    const m = mid.get(h.r);
    const p = hexToWorld(h.q, h.r);
    // The square at the foot: the two bottom rows, wider.
    const wide = h.r >= R - 2 ? 2500 : 1000;
    const mm = m ?? mid.get(R - 1) ?? 0;
    if ((m !== undefined || h.r >= R - 1) && Math.abs(p.x - mm) <= wide) {
      lane.push(h);
      continue;
    }
    const a = alleyRows.find((o) => o.r === h.r);
    if (a && m !== undefined && (p.x - m) * a.side > 1000 && (p.x - m) * a.side <= 3000) {
      alleys.push(h);
      continue;
    }
    rocks.push(h);
  }
  const way: { x: number; y: number }[] = [];
  const rows: number[] = [];
  for (let r = R - 1; r >= -(R - 1); r--) rows.push(r);
  for (const r of rows) way.push({ x: mid.get(r) as number, y: r * ROW });
  const dens = alleyRows
    .filter((a) => mid.has(a.r))
    .map((a) => ({ x: (mid.get(a.r) as number) + a.side * 3000, y: a.r * ROW, at: rows.indexOf(a.r) }));
  rocks.sort(sortHex);
  lane.sort(sortHex);
  alleys.sort(sortHex);
  const top = way[way.length - 1] as { x: number; y: number };
  const foot = way[0] as { x: number; y: number };
  return { radius: R, rocks, lane, alleys, way, dens, arch: { x: top.x, y: top.y - ROW }, start: { x: foot.x, y: foot.y + 300 } };
};

/* ---------- the beam ---------- */

/** A mirror's position (0–2) turns the beam this many sixths: to one side, to the other, back. */
export const MIRROR_TURN = [1, 5, 3] as const;

export interface BeamBoard {
  hooks: { q: number; r: number; dir: number }[];
  mirrors: { q: number; r: number }[];
  lamp: { q: number; r: number };
  walls: { q: number; r: number }[];
  radius: number;
}

export interface Beam {
  /** Its corners as cells: where it leaves, every mirror it turns at, where it stops. */
  pts: Hex[];
  /** Every cell it crosses (for its burn). */
  cells: Hex[];
  /** The mirrors it went by, in order (a mirror twice counts twice). */
  by: number[];
  lit: boolean;
}

/** The beam from the lantern on hook `hook` with the mirrors as `turns` (none with the lantern on the stretcher). */
export const beamTrace = (B: BeamBoard, hook: number, turns: readonly number[]): Beam => {
  const out: Beam = { pts: [], cells: [], by: [], lit: false };
  const h = B.hooks[hook];
  if (!h) return out;
  let q = h.q;
  let r = h.r;
  let d = h.dir;
  out.pts.push({ q, r });
  const seen = new Set<string>();
  for (let step = 0; step < 64; step++) {
    const D = DIRS[d] as Hex;
    q += D.q;
    r += D.r;
    out.cells.push({ q, r });
    if (hexLen(q, r) > B.radius) {
      out.pts.push({ q, r });
      return out;
    }
    if (q === B.lamp.q && r === B.lamp.r) {
      out.pts.push({ q, r });
      out.lit = true;
      return out;
    }
    if (q === h.q && r === h.r) {
      out.pts.push({ q, r });
      return out;
    }
    const m = B.mirrors.findIndex((o) => o.q === q && o.r === r);
    if (m >= 0) {
      out.pts.push({ q, r });
      out.by.push(m);
      const k = `${m}:${d}`;
      // Two mirrors throwing it back and forth: it stops where it would go round again.
      if (seen.has(k)) return out;
      seen.add(k);
      d = (d + (MIRROR_TURN[(turns[m] ?? 0) % 3] as number)) % 6;
      continue;
    }
    if (B.walls.some((w) => w.q === q && w.r === r)) {
      out.pts.push({ q, r });
      return out;
    }
  }
  out.pts.push({ q, r });
  return out;
};

/** Every way of setting the chapel (a hook, each mirror's position) that lights the lamp: [hook, turn0, turn1]. */
export const chapelSolve = (B: BeamBoard): number[][] => {
  const out: number[][] = [];
  for (let hk = 0; hk < B.hooks.length; hk++)
    for (let a = 0; a < 3; a++)
      for (let b = 0; b < 3; b++) {
        const t = [a, b];
        if (beamTrace(B, hk, t).lit) out.push([hk, a, b]);
      }
  return out;
};

/* ---------- the chapel at the crossroads ---------- */

export interface ChapelLayout extends BeamBoard {
  /** The chapel's walls (its front row holds the lamp, its door under it). */
  chapel: Hex[];
  /** Low terrace walls (drawn, walked over: the beam goes over them). */
  terraces: Hex[];
  rocks: Hex[];
  /** Where the stretcher rests (by the bottom) and the hero starts. */
  rest: { x: number; y: number };
  start: { x: number; y: number };
  /** Where the hero stands to work each hook and mirror (a cell next to it). */
  hookStand: { x: number; y: number }[];
  mirrorStand: { x: number; y: number }[];
  /** The mirrors' first positions (never the answer), and the answer: [hook, turn0, turn1]. */
  start0: number[];
  answer: number[];
  /** The hook that seems nearer (the lamp straight up its line): the wrong one. */
  decoy: number;
}

/** The lamp over the chapel's door: the middle of its front row. */
const LAMP: Hex = { q: 2, r: -4 };

export const chapelLayout = (seed: number): ChapelLayout => {
  const R = CHAPEL_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xc4a9));
  // The chapel: its front row (the lamp in its middle) and what is behind it, the middle of the board's top.
  const chapel: Hex[] = [];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    if (h.r <= LAMP.r && Math.abs(p.x) <= 2600) chapel.push(h);
  }
  const inChapel = (h: Hex): boolean => chapel.some((c) => c.q === h.q && c.r === h.r);
  const ok = (h: Hex): boolean => hexLen(h.q, h.r) <= R - 1 && h.r > LAMP.r && h.r <= 3;
  const add = (a: Hex, d: number, k: number): Hex => ({ q: a.q + (DIRS[d] as Hex).q * k, r: a.r + (DIRS[d] as Hex).r * k });
  const sub = (a: Hex, d: number, k: number): Hex => add(a, (d + 3) % 6, k);
  // Every chapel this layout can make, then one of them by the seed.
  type Cand = { A: Hex; B: Hex; M1: Hex; M2: Hex; dA: number; dB: number };
  const cands: Cand[] = [];
  for (const d2 of [1, 2])
    for (const k2 of [2, 3])
      for (const t1 of [1, 5])
        for (const k1 of [2, 3, 4])
          for (const t0 of [1, 5])
            for (const k0 of [2, 3, 4])
              for (const kb of [2, 3]) {
                const M2 = sub(LAMP, d2, k2);
                const d1 = (d2 - t1 + 6) % 6;
                const M1 = sub(M2, d1, k1);
                const d0 = (d1 - t0 + 6) % 6;
                const A = sub(M1, d0, k0);
                const B = sub(M2, d2, kb);
                const all = [A, B, M1, M2];
                if (!all.every(ok)) continue;
                // Apart from each other (each its own post, room to stand by it).
                if (all.some((a, i) => all.some((b, j) => j > i && hexDist(a, b) < 2))) continue;
                // The wrong hook looks nearer to the lamp.
                if (hexDist(B, LAMP) >= hexDist(A, LAMP)) continue;
                // One way of the eighteen lights the lamp (from the far hook), and its beam runs over no other post.
                const walls = chapel.filter((h) => !(h.q === LAMP.q && h.r === LAMP.r));
                const hooks = [
                  { ...A, dir: d0 },
                  { ...B, dir: d2 },
                ];
                const B0: BeamBoard = { hooks, mirrors: [M1, M2], lamp: LAMP, walls, radius: R };
                const sol = chapelSolve(B0);
                if (sol.length !== 1 || sol[0]?.[0] !== 0) continue;
                const run = beamTrace(B0, 0, [sol[0][1] as number, sol[0][2] as number]);
                if (run.cells.some((x) => x.q === B.q && x.r === B.r)) continue;
                cands.push({ A, B, M1, M2, dA: d0, dB: d2 });
              }
  const pick = cands[rngInt(rng, cands.length)] ?? cands[0];
  const c = pick as Cand;
  // Hooks in a fixed order on the board (left first), so the marks read the same way every time.
  const hooks0 = [
    { ...c.A, dir: c.dA },
    { ...c.B, dir: c.dB },
  ].sort((a, b) => hexToWorld(a.q, a.r).x - hexToWorld(b.q, b.r).x);
  const decoy = hooks0.findIndex((h) => h.q === c.B.q && h.r === c.B.r);
  const mirrors = [c.M1, c.M2].map((m) => ({ q: m.q, r: m.r })).sort((a, b) => hexToWorld(a.q, a.r).x - hexToWorld(b.q, b.r).x);
  const walls = chapel.filter((h) => !(h.q === LAMP.q && h.r === LAMP.r)).sort(sortHex);
  const board: BeamBoard = { hooks: hooks0, mirrors, lamp: { ...LAMP }, walls, radius: R };
  const answers = chapelSolve(board);
  const answer = (answers[0] ?? [0, 0, 0]) as number[];
  // The mirrors start anywhere but the answer.
  let start0 = [rngInt(rng, 3), rngInt(rng, 3)];
  if (start0[0] === answer[1] && start0[1] === answer[2]) start0 = [(start0[0] as number + 1) % 3, start0[1] as number];
  // Terrace walls: short rows of stone across the terraces (walked over, low), clear of the posts.
  const posts = [...hooks0, ...mirrors];
  const terraces: Hex[] = [];
  for (const h of allCells(R)) {
    if (inChapel(h) || posts.some((p) => p.q === h.q && p.r === h.r)) continue;
    if ((h.r === 1 || h.r === -2 || h.r === 4) && hexLen(h.q, h.r) >= R - 1) terraces.push(h);
  }
  // Where to stand by a post: the open cell next to it nearest the stretcher's rest.
  const rest = hexToWorld(0, 4);
  const blocked = (h: Hex): boolean => inChapel(h) || posts.some((p) => p.q === h.q && p.r === h.r) || hexLen(h.q, h.r) > R;
  const stand = (p: Hex): { x: number; y: number } => {
    let best: { x: number; y: number } | null = null;
    let bd = 0x7fffffff;
    for (const D of DIRS) {
      const n = { q: p.q + D.q, r: p.r + D.r };
      if (blocked(n)) continue;
      const w = hexToWorld(n.q, n.r);
      const d = dist2(w.x, w.y, rest.x, rest.y);
      if (d < bd) {
        bd = d;
        best = w;
      }
    }
    return best ?? hexToWorld(p.q, p.r + 1);
  };
  const rocks = [...chapel, ...posts.map((p) => ({ q: p.q, r: p.r }))].sort(sortHex);
  return {
    ...board,
    chapel: chapel.sort(sortHex),
    terraces: terraces.sort(sortHex),
    rocks,
    rest: { x: rest.x, y: rest.y },
    start: { x: rest.x + 1200, y: rest.y - 400 },
    hookStand: hooks0.map(stand),
    mirrorStand: mirrors.map(stand),
    start0,
    answer,
    decoy,
  };
};

/* ---------- the slope of the limpets ---------- */

export interface BendsLayout {
  radius: number;
  rocks: Hex[];
  /** The way's cells (the legs and the bends), and the slope between (rock, drawn as the hill). */
  lane: Hex[];
  /** The stretcher's way: its corners from the foot to the cliff road. */
  way: { x: number; y: number }[];
  /** The bends (their outer point) and the way's corner each is at. */
  bends: { x: number; y: number; at: number }[];
  /** The ground by the way where limpets come out between the bends: a point and the corner it is by. */
  dens: { x: number; y: number; at: number }[];
  /** The top: where the cliff road goes on. */
  top: { x: number; y: number };
  start: { x: number; y: number };
}

/** The legs' rows from the foot up, and how far across they run. */
const LEG_ROWS = [9, 4, -1, -6];
const LEG_X = 5000;

export const bendsLayout = (seed: number): BendsLayout => {
  const R = BENDS_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0x51e9));
  // Which way the first leg runs (the bends alternate after it).
  const side = rngInt(rng, 2) === 0 ? 1 : -1;
  const way: { x: number; y: number }[] = [];
  const bends: { x: number; y: number; at: number }[] = [];
  LEG_ROWS.forEach((r, i) => {
    const s = i % 2 === 0 ? side : -side;
    const y = r * ROW;
    // A little play in where each leg begins and ends.
    const jog = (rngInt(rng, 3) - 1) * 500;
    way.push({ x: -s * LEG_X + jog, y });
    way.push({ x: s * LEG_X + jog, y });
    const next = LEG_ROWS[i + 1];
    if (next !== undefined) {
      // The bend: out past the leg's end and up to the next.
      const by = idiv((r + next) * ROW, 2);
      way.push({ x: s * (LEG_X + 1500) + jog, y: by });
      bends.push({ x: s * (LEG_X + 1500) + jog, y: by, at: way.length - 1 });
    }
  });
  // The last leg's end, then up to the cliff road at the top.
  const last = way[way.length - 1] as { x: number; y: number };
  const top = { x: last.x - (last.x > 0 ? 1000 : -1000), y: -9 * ROW };
  way.push(top);
  // The way's cells: within a hex and a bit of its line.
  const lane: Hex[] = [];
  const rocks: Hex[] = [];
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    let best = 0x7fffffff;
    for (let i = 0; i + 1 < way.length; i++) {
      const a = way[i] as { x: number; y: number };
      const b = way[i + 1] as { x: number; y: number };
      const d = segD2(p.x, p.y, a.x, a.y, b.x, b.y);
      if (d < best) best = d;
    }
    // (And a little landing at the foot.)
    const foot = way[0] as { x: number; y: number };
    if (best <= 1150 * 1150 || dist2(p.x, p.y, foot.x, foot.y) <= 1900 * 1900) lane.push(h);
    else rocks.push(h);
  }
  // Ground for the limpets between the bends: along each leg, at its third and two thirds.
  const dens: { x: number; y: number; at: number }[] = [];
  for (let i = 0; i + 1 < way.length; i++) {
    const a = way[i] as { x: number; y: number };
    const b = way[i + 1] as { x: number; y: number };
    if (a.y !== b.y) continue;
    for (const k of [1, 2]) dens.push({ x: a.x + idiv((b.x - a.x) * k, 3), y: a.y + 700, at: i });
  }
  lane.sort(sortHex);
  rocks.sort(sortHex);
  const foot = way[0] as { x: number; y: number };
  return { radius: R, rocks, lane, way, bends, dens, top, start: { x: foot.x - side * 1400, y: foot.y + 500 } };
};
